import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import {
  TrendingUp, TrendingDown, Receipt, ShoppingCart, AlertCircle, Wallet,
  ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";

const fmt = (n, c = "S/") => `${c} ${Number(n || 0).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function Kpi({ label, value, icon: Icon, trend, testid, accent = "blue" }) {
  const accents = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <div data-testid={testid} className="bg-white border border-slate-200 rounded-md p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</span>
        <div className={`w-7 h-7 rounded flex items-center justify-center border ${accents[accent]}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
      </div>
      <div className="text-xl font-bold tracking-tight text-slate-900 tabular">{value}</div>
      {trend != null && (
        <div className={`flex items-center gap-1 text-[11px] mt-1 ${trend >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          <span className="tabular">{Math.abs(trend).toFixed(1)}%</span>
          <span className="text-slate-500">vs mes anterior</span>
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { empresa } = useApp();
  const [data, setData] = useState(null);
  const [periodo, setPeriodo] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });

  useEffect(() => {
    if (!empresa) return;
    api.get(`/dashboard?periodo=${periodo}`).then((r) => setData(r.data)).catch(() => {});
  }, [empresa, periodo]);

  if (!data) return <div className="text-sm text-slate-500">Cargando indicadores...</div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Dashboard</h1>
          <p className="text-[13px] text-slate-500">Resumen contable y financiero del periodo seleccionado</p>
        </div>
        <input
          data-testid="periodo-input"
          type="month"
          value={periodo}
          onChange={(e) => setPeriodo(e.target.value)}
          className="h-9 border border-slate-200 rounded px-2 text-[13px] bg-white"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi testid="kpi-ventas" label="Ventas del periodo" value={fmt(data.total_ventas)} icon={Receipt} accent="emerald" />
        <Kpi testid="kpi-compras" label="Compras del periodo" value={fmt(data.total_compras)} icon={ShoppingCart} accent="blue" />
        <Kpi testid="kpi-igv" label="IGV a pagar" value={fmt(data.igv_pagar)} icon={AlertCircle} accent={data.igv_pagar >= 0 ? "amber" : "emerald"} />
        <Kpi testid="kpi-utilidad" label="Utilidad bruta" value={fmt(data.utilidad_bruta)} icon={TrendingUp} accent={data.utilidad_bruta >= 0 ? "emerald" : "rose"} />
        <Kpi testid="kpi-cxc" label="Cuentas por cobrar" value={fmt(data.cuentas_cobrar)} icon={Wallet} accent="slate" />
        <Kpi testid="kpi-cxp" label="Cuentas por pagar" value={fmt(data.cuentas_pagar)} icon={Wallet} accent="slate" />
        <Kpi testid="kpi-num-ventas" label="N° comprobantes venta" value={data.num_ventas} icon={Receipt} accent="blue" />
        <Kpi testid="kpi-num-compras" label="N° comprobantes compra" value={data.num_compras} icon={ShoppingCart} accent="blue" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-slate-800">Evolución de ventas (diaria)</h3>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">S/ Soles</span>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={data.serie_ventas}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="fecha" tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(d) => d.slice(8)} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip contentStyle={{ fontSize: 11, borderRadius: 4 }} formatter={(v) => fmt(v)} />
              <Line type="monotone" dataKey="total" stroke="#2563eb" strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4">
          <h3 className="text-sm font-bold text-slate-800 mb-3">Comparativo Ventas vs Compras</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={[{ k: "Ventas", v: data.total_ventas }, { k: "Compras", v: data.total_compras }]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="k" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip contentStyle={{ fontSize: 11 }} formatter={(v) => fmt(v)} />
              <Bar dataKey="v" fill="#0f172a" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-4">
        <h3 className="text-sm font-bold text-slate-800 mb-3">Resumen IGV</h3>
        <div className="grid grid-cols-3 gap-4 text-[13px]">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">IGV Ventas (Débito)</div>
            <div className="text-base font-bold text-slate-900 tabular">{fmt(data.igv_ventas)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">IGV Compras (Crédito)</div>
            <div className="text-base font-bold text-slate-900 tabular">{fmt(data.igv_compras)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Saldo a Pagar</div>
            <div className={`text-base font-bold tabular ${data.igv_pagar >= 0 ? "text-rose-600" : "text-emerald-600"}`}>{fmt(data.igv_pagar)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
